import { type ReactNode, useCallback, useEffect, useMemo, useState } from 'react'

import { MeetingsMeetingGetTranscriptDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useQuery } from 'modules/core/hooks/useQuery'

import { useMeeting } from 'modules/meetings/lib/hooks/useMeeting'
import { type Transcript } from 'modules/meetings/lib/utils/transcriptUtils'

import { MeetingTranscriptContext, type MeetingTranscriptContextState } from './index'

interface MeetingTranscriptProviderProps {
  children: ReactNode
}

export const MeetingTranscriptProvider = ({ children }: MeetingTranscriptProviderProps) => {
  const { meetingId } = useMeeting()
  const [transcript, setTranscript] = useState<Transcript | null>(null)
  const {
    company: { id: companyId },
  } = useCurrentSession()

  // Query to get transcript data
  const {
    data: transcriptData,
    loading: loadingTranscript,
    networkActivity: networkActivityTranscript,
    refetch: refetchTranscriptQuery,
  } = useQuery(MeetingsMeetingGetTranscriptDocument, {
    variables: {
      meetingId: String(meetingId ?? '0'),
      companyId: companyId,
    },
    skip: !meetingId,
  })

  // Process transcript data
  useEffect(() => {
    if (transcriptData) {
      const rawTranscript = transcriptData?.meetings.transcriptsConnection.nodes[0]
      const processedTranscript =
        rawTranscript?.utterances
          ?.map((utterance, index) => {
            const inPersonValue = rawTranscript?.utterancesInPerson?.[index]
            // Convert integer to boolean: 1 = in-person, 0 = remote
            // Handle both number and string (RR serialization workaround)
            const inPerson = inPersonValue === 1

            return {
              utterance: utterance,
              attendeeId: String(rawTranscript?.utterancesAttendeeIds[index] || ''),
              timestamp: String(rawTranscript?.utterancesTimestamps[index] || ''),
              inPerson,
            }
          })
          .filter((utterance) => utterance.utterance !== '') || []

      // Merge consecutive utterances and set transcript
      setTranscript(processedTranscript)
    }
  }, [transcriptData])

  const refetchTranscript = useCallback(async () => {
    await refetchTranscriptQuery()
  }, [refetchTranscriptQuery])

  // Memoize the context value to prevent unnecessary re-renders
  const contextValue: MeetingTranscriptContextState = useMemo(
    () => ({
      transcript,
      loadingTranscript: loadingTranscript || networkActivityTranscript,
      refetchTranscript,
      transcriptExpirationTime:
        transcriptData?.meetings.settingsConnection.nodes[0]?.transcriptExpirationTime ?? null,
    }),
    [transcript, loadingTranscript, networkActivityTranscript, refetchTranscript, transcriptData]
  )

  return (
    <MeetingTranscriptContext.Provider value={contextValue}>
      {children}
    </MeetingTranscriptContext.Provider>
  )
}
